import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import HelpArticleContent from '../components/HelpArticleContent';
import {
  Search,
  Book,
  Calendar,
  Users,
  Truck,
  Building2,
  FileText,
  Settings,
  Smartphone,
  ClipboardList,
  ChevronRight,
  ChevronLeft,
  PlayCircle,
  HelpCircle,
  Database,
  Clock,
  Shield,
  Loader2,
  Receipt,
  BarChart3,
  MessageSquare,
  UserCog,
  Link2,
  Factory,
  Ticket,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { TrainingSection } from '../components/tour';

// Icon mapping for database content
const ICON_MAP = {
  PlayCircle,
  ClipboardList,
  Calendar,
  Building2,
  Truck,
  Users,
  FileText,
  Settings,
  Smartphone,
  Database,
  Book,
  HelpCircle,
  Clock,
  Shield,
  Receipt,
  BarChart3,
  MessageSquare,
  UserCog,
  Link2,
  Factory
};

// Fallback content (minimal - database should have full content)
const HELP_CONTENT = {
  gettingStarted: {
    title: "Getting Started",
    icon: PlayCircle,
    description: "Learn the basics of EQUIPR CORE",
    sections: [
      {
        id: "what-is-equipr",
        title: "What is EQUIPR CORE?",
        content: "EQUIPR CORE is a comprehensive equipment allocation and scheduling platform designed for crane hire and earthmoving businesses."
      }
    ]
  }
};

// Role-based content tags
const ROLE_CONTENT = {
  admin: ['settings', 'dataManagement', 'gettingStarted', 'workflows', 'schedule', 'customers', 'equipment', 'operators', 'dockets'],
  dispatcher: ['gettingStarted', 'workflows', 'schedule', 'customers', 'equipment', 'operators', 'dockets'],
  operator: ['gettingStarted', 'mobile']
};

const HelpCenter = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dbContent, setDbContent] = useState(null);
  const [activeTab, setActiveTab] = useState('documentation'); // Only documentation for now (training hidden)
  
  // Navigation state - null = categories view, string = category key or article view
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Fetch content from database
  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/help/content');
      if (response.data.from_database && response.data.categories.length > 0) {
        setDbContent(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch help content:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Handle article deep-linking via URL parameter
  useEffect(() => {
    const articleParam = searchParams.get('article');
    const categoryParam = searchParams.get('category');
    
    if (categoryParam && dbContent?.from_database) {
      setSelectedCategory(categoryParam);
      if (articleParam) {
        const article = dbContent.articles.find(a => a.key === articleParam && a.category_key === categoryParam);
        if (article) {
          setSelectedArticle(article);
        }
      }
      searchParams.delete('article');
      searchParams.delete('category');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, dbContent, setSearchParams]);

  // Transform database content to match the expected format
  const transformedContent = useMemo(() => {
    if (!dbContent || !dbContent.from_database) {
      return HELP_CONTENT;
    }

    const result = {};
    dbContent.categories.forEach(cat => {
      const categoryArticles = dbContent.articles.filter(a => a.category_key === cat.key);
      result[cat.key] = {
        title: cat.title,
        icon: ICON_MAP[cat.icon] || Book,
        description: cat.description,
        roles: cat.roles,
        sections: categoryArticles.map(article => ({
          id: article.id,
          key: article.key,
          title: article.title,
          content: article.content,
          content_format: article.content_format,
          badge: article.badge
        }))
      };
    });
    return result;
  }, [dbContent]);

  // Filter content based on search and role
  const filteredContent = useMemo(() => {
    let content = { ...transformedContent };
    
    // Filter by role
    if (roleFilter !== 'all') {
      if (dbContent?.from_database) {
        content = Object.fromEntries(
          Object.entries(content).filter(([, category]) => 
            category.roles?.includes(roleFilter)
          )
        );
      } else {
        const allowedCategories = ROLE_CONTENT[roleFilter] || [];
        content = Object.fromEntries(
          Object.entries(content).filter(([key]) => allowedCategories.includes(key))
        );
      }
    }
    
    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      content = Object.fromEntries(
        Object.entries(content).map(([key, category]) => {
          const filteredSections = category.sections.filter(section =>
            section.title.toLowerCase().includes(query) ||
            section.content.toLowerCase().includes(query)
          );
          return [key, { ...category, sections: filteredSections }];
        }).filter(([, category]) => category.sections.length > 0)
      );
    }
    
    return content;
  }, [searchQuery, roleFilter, transformedContent, dbContent]);

  // Render markdown-like content
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentList = null;
    let currentListType = null;
    let listItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        if (currentListType === 'ordered') {
          elements.push(
            <ol key={`ol-${elements.length}`} className="list-decimal list-inside space-y-1 mb-3 ml-1">
              {listItems}
            </ol>
          );
        } else {
          elements.push(
            <ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-1 mb-3 ml-1">
              {listItems}
            </ul>
          );
        }
        listItems = [];
        currentListType = null;
      }
    };

    lines.forEach((line, i) => {
      // Check for numbered list item
      const numberedMatch = line.match(/^(\d+)\.\s(.+)/);
      if (numberedMatch) {
        if (currentListType !== 'ordered') {
          flushList();
          currentListType = 'ordered';
        }
        // Parse inline bold in list item
        const itemContent = numberedMatch[2];
        const parsedContent = parseInlineBold(itemContent);
        listItems.push(
          <li key={`li-${i}`}>{parsedContent}</li>
        );
        return;
      }

      // Check for bullet list item
      if (line.startsWith('- ') || line.startsWith('• ')) {
        if (currentListType !== 'unordered') {
          flushList();
          currentListType = 'unordered';
        }
        const itemContent = line.substring(2);
        const parsedContent = parseInlineBold(itemContent);
        listItems.push(
          <li key={`li-${i}`}>{parsedContent}</li>
        );
        return;
      }

      // Not a list item - flush any pending list
      flushList();

      // Bold header (entire line)
      if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <h4 key={`h-${i}`} className="font-semibold mt-4 mb-2">{line.replace(/\*\*/g, '')}</h4>
        );
        return;
      }

      // Line with inline bold
      if (line.includes('**')) {
        elements.push(
          <p key={`p-${i}`} className="mb-2">{parseInlineBold(line)}</p>
        );
        return;
      }

      // Empty line
      if (!line.trim()) {
        elements.push(<div key={`sp-${i}`} className="h-2" />);
        return;
      }

      // Regular paragraph
      elements.push(<p key={`t-${i}`} className="mb-2">{line}</p>);
    });

    // Flush any remaining list
    flushList();

    return elements;
  };

  // Helper to parse inline bold text
  const parseInlineBold = (text) => {
    if (!text.includes('**')) return text;
    
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((part, j) => 
      j % 2 === 1 ? <strong key={j}>{part}</strong> : part
    );
  };

  // Navigation handlers
  const handleCategoryClick = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedArticle(null);
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedArticle(null);
  };

  const handleBackToArticles = () => {
    setSelectedArticle(null);
  };

  const categories = Object.entries(filteredContent);
  const currentCategory = selectedCategory ? filteredContent[selectedCategory] : null;

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 mb-2">
            <Book className="h-8 w-8 text-[#ffd108]" />
            <h1 className="text-3xl font-heading font-bold">Help Centre</h1>
          </div>
          <Button 
            onClick={() => navigate('/support')}
            className="btn-volt"
            data-testid="support-button"
          >
            <Ticket className="h-4 w-4 mr-2" />
            Support
          </Button>
        </div>
        <p className="text-muted-foreground">
          Learn how to use EQUIPR CORE with guides, tutorials, and reference documentation
        </p>
      </div>

      {/* Documentation Content - Training tab hidden for now due to screen ratio issues */}
      {activeTab === 'documentation' && (
        <>
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Reset navigation when searching
                  if (e.target.value) {
                    setSelectedCategory(null);
                    setSelectedArticle(null);
                  }
                }}
                className="pl-10"
                data-testid="help-search"
              />
            </div>
            <Tabs value={roleFilter} onValueChange={setRoleFilter} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="all">All Roles</TabsTrigger>
                <TabsTrigger value="admin">Admin</TabsTrigger>
                <TabsTrigger value="dispatcher">Dispatcher</TabsTrigger>
                <TabsTrigger value="operator">Operator</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {/* Navigation Panel */}
        <div className="lg:col-span-1">
          <Card className="min-h-[500px] lg:min-h-[calc(100vh-280px)] flex flex-col">
            <CardHeader className="pb-3 flex-shrink-0">
              {/* Breadcrumb / Back Navigation */}
              {selectedCategory && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToCategories}
                  className="mb-2 -ml-2 text-muted-foreground hover:text-foreground"
                  data-testid="help-back-button"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  All Categories
                </Button>
              )}
              <CardTitle className="text-sm font-medium">
                {!selectedCategory && 'Categories'}
                {selectedCategory && currentCategory?.title}
              </CardTitle>
              {selectedCategory && currentCategory && (
                <CardDescription className="text-xs mt-1">
                  {currentCategory.description}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="space-y-1 p-2">
                  {/* Show Categories */}
                  {!selectedCategory && categories.map(([key, category]) => {
                    const Icon = category.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => handleCategoryClick(key)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-left transition-colors hover:bg-muted group"
                        data-testid={`help-category-${key}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-[#ffd108]/10 flex items-center justify-center flex-shrink-0">
                            <Icon className="h-4 w-4 text-[#ffd108]" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{category.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {category.sections.length} {category.sections.length === 1 ? 'article' : 'articles'}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" />
                      </button>
                    );
                  })}

                  {/* Show Articles in Category - always show when category is selected */}
                  {selectedCategory && currentCategory?.sections.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => handleArticleClick(article)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                        selectedArticle?.id === article.id
                          ? 'bg-[#ffd108] text-black'
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`help-article-${article.id}`}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        selectedArticle?.id === article.id ? 'bg-black' : 'bg-[#ffd108]'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm ${selectedArticle?.id === article.id ? 'font-medium' : ''}`}>
                          {article.title}
                        </p>
                        {article.badge && (
                          <Badge 
                            variant="secondary" 
                            className={`text-xs mt-1 ${
                              selectedArticle?.id === article.id 
                                ? 'bg-black/20 text-black' 
                                : 'bg-[#ffd108]/20 text-[#ffd108]'
                            }`}
                          >
                            {article.badge}
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))}

                  {/* No results */}
                  {categories.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <HelpCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No results found</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3 xl:col-span-4">
          <Card className="min-h-[500px] lg:min-h-[calc(100vh-280px)] flex flex-col">
            {/* Welcome / Category Overview */}
            {!selectedCategory && (
              <>
                <CardHeader>
                  <CardTitle className="text-xl">Welcome to the Help Center</CardTitle>
                  <CardDescription>
                    Select a category from the left to browse articles, or use the search bar to find specific topics.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Quick Start Cards */}
                    <Card 
                      className="hover:border-[#ffd108] transition-colors cursor-pointer"
                      onClick={() => {
                        handleCategoryClick('getting-started');
                      }}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <PlayCircle className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">Getting Started</p>
                          <p className="text-sm text-muted-foreground">New to EQUIPR CORE?</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card 
                      className="hover:border-[#ffd108] transition-colors cursor-pointer"
                      onClick={() => handleCategoryClick('workflows')}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">Common Workflows</p>
                          <p className="text-sm text-muted-foreground">Step-by-step guides</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card 
                      className="hover:border-[#ffd108] transition-colors cursor-pointer"
                      onClick={() => handleCategoryClick('mobile-app')}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                          <Smartphone className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">Mobile App</p>
                          <p className="text-sm text-muted-foreground">Guide for operators</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card 
                      className="hover:border-[#ffd108] transition-colors cursor-pointer"
                      onClick={() => handleCategoryClick('troubleshooting')}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                          <HelpCircle className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium">Troubleshooting</p>
                          <p className="text-sm text-muted-foreground">Common issues & FAQ</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mt-8">
                    <h3 className="font-medium mb-4">All Categories</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
                      {categories.map(([key, category]) => {
                        const Icon = category.icon;
                        return (
                          <button
                            key={key}
                            onClick={() => handleCategoryClick(key)}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted text-left text-sm"
                          >
                            <Icon className="h-4 w-4 text-[#ffd108]" />
                            <span className="truncate">{category.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </>
            )}

            {/* Category Articles List (when category selected but no article) */}
            {selectedCategory && !selectedArticle && currentCategory && (
              <>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {currentCategory.icon && <currentCategory.icon className="h-6 w-6 text-[#ffd108]" />}
                    <div>
                      <CardTitle className="text-xl">{currentCategory.title}</CardTitle>
                      <CardDescription>{currentCategory.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-auto">
                  <p className="text-sm text-muted-foreground mb-4">
                    Select an article from the left panel to read more.
                  </p>
                  <div className="space-y-2">
                    {currentCategory.sections.map((article) => (
                      <button
                        key={article.id}
                        onClick={() => handleArticleClick(article)}
                        className="w-full flex items-center justify-between p-4 rounded-lg border hover:border-[#ffd108] hover:bg-muted/50 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#ffd108]/10 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-4 w-4 text-[#ffd108]" />
                          </div>
                          <div>
                            <p className="font-medium">{article.title}</p>
                            {article.badge && (
                              <Badge variant="secondary" className="bg-[#ffd108]/20 text-[#ffd108] text-xs mt-1">
                                {article.badge}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-[#ffd108]" />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </>
            )}

            {/* Article Content */}
            {selectedArticle && (
              <>
                <CardHeader className="flex-shrink-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl">{selectedArticle.title}</CardTitle>
                      {selectedArticle.badge && (
                        <Badge variant="secondary" className="bg-[#ffd108]/20 text-[#ffd108] mt-2">
                          {selectedArticle.badge}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-auto">
                  <ScrollArea className="h-full pr-4">
                    <HelpArticleContent
                      content={selectedArticle.content}
                      format={selectedArticle.content_format || 'markdown'}
                      className="dark:prose-invert"
                    />
                  </ScrollArea>
                </CardContent>
              </>
            )}

            {/* No Category Selected and No Content */}
            {selectedCategory && !currentCategory && (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Category not found</h3>
                  <p className="text-muted-foreground mb-4">
                    This category may have been removed or is not available.
                  </p>
                  <Button onClick={handleBackToCategories}>
                    Back to Categories
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default HelpCenter;
